import { skipToken, useQuery, type QueryClient } from '@tanstack/react-query';
import type { UploadKind } from '@chatapp-inbox/shared';
import { revokePreview } from './attachments';

/**
 * Envios feitos nesta aba que o servidor ainda não confirmou (ou recusou). Moram no cache do
 * QueryClient, FORA da chave da sessão: o logout e a troca de conta os descartam junto com o resto,
 * então o texto de um atendente nunca aparece para o próximo da mesma aba. Também por isso não
 * ficam em variável de módulo nem em localStorage.
 *
 * Ficam separados do cache de mensagens do servidor de propósito: um refetch (ou, na T26, um
 * evento do tempo real) reescreve aquele cache e levaria os envios em andamento junto.
 */
export type LocalState =
  /** Esperando a vez: os envios de uma conversa saem um de cada vez, para manter a ordem. */
  | 'queued'
  /** O pedido está em andamento. */
  | 'sending'
  /** Sem resposta do servidor (rede, tempo limite, 5xx): NÃO se sabe se a mensagem chegou. */
  | 'unconfirmed'
  /** O servidor respondeu que não vai enviar (4xx): o texto precisa ser editado ou descartado. */
  | 'rejected';

/** O arquivo de um envio local: sobe primeiro (com progresso) e depois é citado na mensagem. */
export interface LocalAttachment {
  /** O arquivo escolhido; fica na memória desta aba até o envio sair da tela. */
  file: File;
  kind: UploadKind;
  mimeType: string;
  /** Miniatura da imagem (`URL.createObjectURL`): revogada quando o envio sai da tela. */
  previewUrl: string | null;
  /** Id do arquivo no servidor depois do upload; reaproveitado ao tentar de novo (não sobe de novo). */
  attachmentId: string | null;
  /** Progresso do upload, de 0 a 1. */
  progress: number;
  /** Nota de voz gravada aqui (vai como mensagem de voz do WhatsApp). */
  voice?: boolean;
}

export interface LocalSend {
  /** Identifica o envio: reenviar com o MESMO id nunca duplica a mensagem no servidor. */
  clientId: string;
  /** Texto já aparado nas pontas, exatamente o que vai ao servidor (a legenda, se há arquivo). */
  text: string;
  /** O arquivo a enviar junto, se houver. */
  attachment?: LocalAttachment;
  /** Quando a pessoa enviou (relógio local, ISO). */
  createdAt: string;
  state: LocalState;
  /** Início da tentativa atual (ms): de lá se conta o "Ainda enviando…". */
  attemptedAt: number;
  /**
   * `createdAt` (relógio do SERVIDOR) da mensagem mais recente que o cache tinha quando o envio
   * nasceu. A mensagem do servidor só pode ser a deste envio se for mais nova que isso.
   */
  after: string | null;
  /** Motivo mostrado quando `state` é `rejected`. */
  error: string | null;
  /** Em `rejected`: o servidor pediu para tentar mais tarde, então "Tentar de novo" faz sentido. */
  retryable: boolean;
}

const EMPTY: readonly LocalSend[] = Object.freeze([]);

/** Id de exibição de um envio local (nunca colide com um id do servidor, que é um UUID). */
export const localId = (clientId: string): string => `local:${clientId}`;

export const outboxKey = (conversationId: string) => ['outbox', conversationId] as const;

export function readOutbox(queryClient: QueryClient, conversationId: string): readonly LocalSend[] {
  return queryClient.getQueryData<readonly LocalSend[]>(outboxKey(conversationId)) ?? EMPTY;
}

/**
 * Atualiza a lista de envios. Se ela não existir mais (logout, troca de conta) NÃO a recria: uma
 * resposta atrasada da sessão antiga não pode pôr texto de outra pessoa no cache da seguinte.
 */
function update(
  queryClient: QueryClient,
  conversationId: string,
  change: (list: readonly LocalSend[]) => readonly LocalSend[],
): void {
  queryClient.setQueryData<readonly LocalSend[]>(outboxKey(conversationId), (cached) =>
    cached ? change(cached) : cached,
  );
}

/** Acrescenta um envio ao fim da fila (cria a lista se for o primeiro). */
export function appendLocal(
  queryClient: QueryClient,
  conversationId: string,
  local: LocalSend,
): void {
  queryClient.setQueryData<readonly LocalSend[]>(outboxKey(conversationId), (cached) => [
    ...(cached ?? EMPTY),
    local,
  ]);
}

export function patchLocal(
  queryClient: QueryClient,
  conversationId: string,
  clientId: string,
  patch: Partial<LocalSend>,
): void {
  update(queryClient, conversationId, (list) =>
    list.some((l) => l.clientId === clientId)
      ? list.map((l) => (l.clientId === clientId ? { ...l, ...patch } : l))
      : list,
  );
}

export function removeLocal(
  queryClient: QueryClient,
  conversationId: string,
  clientId: string,
): void {
  // A miniatura local deixa de ser necessária: a mensagem do servidor (ou nada) toma o lugar dela.
  revokePreview(
    readOutbox(queryClient, conversationId).find((l) => l.clientId === clientId)?.attachment
      ?.previewUrl ?? null,
  );
  update(queryClient, conversationId, (list) =>
    list.some((l) => l.clientId === clientId) ? list.filter((l) => l.clientId !== clientId) : list,
  );
}

/**
 * Os envios da conversa, em tempo de renderização. O observador também mantém a lista viva no
 * cache (`gcTime` infinito) enquanto a pessoa navega por outras conversas e volta; ela só some no
 * logout. Sem `queryFn` (`skipToken`) nada é buscado: o cache é a única fonte.
 */
export function useOutbox(conversationId: string): readonly LocalSend[] {
  const { data } = useQuery<readonly LocalSend[]>({
    queryKey: outboxKey(conversationId),
    queryFn: skipToken,
    initialData: EMPTY,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return data ?? EMPTY;
}
